import './BackgroundJobWait.scss';
import React, { isValidElement, Ref, useLayoutEffect, useRef, useState } from 'react';
import { VisuallyHidden } from '@mantine/core';
import { useElementSize, useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { backgroundJobKind } from '@services/backgroundJobs/backgroundJobKinds';
import BackgroundJobWaitStage, { IBackgroundJobWaitStageProps } from './BackgroundJobWaitStage';
import { backgroundJobWaitAnnouncement, type BackgroundJobRunPhase } from './useBackgroundJobRun';

/** Высота области, пока форма не замерена (та же, что `min-height` в стилях). */
const BODY_MIN_HEIGHT = 212;

export interface IBackgroundJobWaitProps extends Omit<IBackgroundJobWaitStageProps, 'phase'> {
    phase: BackgroundJobRunPhase;
    /** Этап для live-области — без цифр хода (`useBackgroundJobRun().stageName`); без него — `stageText`. */
    stageName?: string;
    /** Форма вернулась из итога («Выбрать другой файл») — входит заново. */
    returned?: boolean;
    /** Форма окна до старта. */
    children?: React.ReactNode;
    /** Корень тела окна (`useBackgroundJobRun().waitRef`). */
    ref?: Ref<HTMLDivElement>;
}

/** Текст подписи для live-области: «Загружаем <b>План.xlsx</b>» → «Загружаем План.xlsx». */
const nodeText = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(nodeText).join('');
    if (isValidElement<{ children?: React.ReactNode }>(node)) return nodeText(node.props.children);
    return '';
};

/**
 * Тело окна, которое ждёт фоновую задачу: форма до старта, лоадер с этапом и прогрессом, итог с файлом и действиями.
 * Лоадер и итог встают в высоту формы — окно не прыгает при смене фаз. Live-область объявляет подпись с этапом, затем
 * итог.
 */
const BackgroundJobWait = ({ phase, stageName, returned, children, ref, ...stage }: IBackgroundJobWaitProps) => {
    const [formHeight, setFormHeight] = useState(BODY_MIN_HEIGHT);
    const { ref: sizeRef, height: innerHeight } = useElementSize();
    const innerElement = useRef<HTMLDivElement>(null);
    const innerRef = useMergedRef(sizeRef, innerElement);

    // Форма ещё на месте, пока уходит: её высота — высота окна на время работы.
    useLayoutEffect(() => {
        if (phase !== 'leaving') return;
        const height = innerElement.current?.offsetHeight ?? 0;
        if (height > 0) setFormHeight(height);
    }, [phase]);

    const showForm = phase === 'idle' || phase === 'leaving';
    const step = stageName ?? stage.stageText ?? backgroundJobKind(stage.kind).wait.runningLabel;
    const announcement = backgroundJobWaitAnnouncement(phase, `${nodeText(stage.subject)}. ${step}`, stage.result);
    return (
        <>
            {/* Одна live-область на всё окно, смонтирована с самого начала: меняется только текст — так его читают. */}
            <VisuallyHidden role="status" aria-live="polite">
                {announcement}
            </VisuallyHidden>
            <div
                ref={ref}
                className="bg-job-wait"
                style={phase === 'idle' ? undefined : { height: Math.max(formHeight, Math.ceil(innerHeight)) }}
            >
                <div ref={innerRef} className="bg-job-wait__inner">
                    {showForm ? (
                        <div
                            className={clsx(
                                'bg-job-wait__form',
                                phase === 'leaving' && 'bg-job-wait__form--leaving',
                                returned && 'bg-job-wait__form--returned',
                            )}
                        >
                            {children}
                        </div>
                    ) : (
                        <BackgroundJobWaitStage phase={phase} {...stage} />
                    )}
                </div>
            </div>
        </>
    );
};

export default BackgroundJobWait;
